import { useEffect, useRef } from 'react'
import { Minus, Plus, Tag, Trash2 } from 'lucide-react'
import clsx from 'clsx'

import { api } from '@/lib/api'
import { cantidadMilesimas, plataCentavos } from '@/lib/formato'
import { totalLinea } from './useTicket'
import type { LineaTicket } from './tipos'

interface Props {
  lineas: LineaTicket[]
  seleccionada: string | null
  onSeleccionar: (uid: string) => void
  onSumar: (uid: string, signo: 1 | -1) => void
  onQuitar: (uid: string) => void
  onEditarCantidad: (uid: string) => void
}

// El ticket que se está armando.
//
// Lo mira el CLIENTE por encima del hombro del cajero, así que la línea tiene
// que decir qué es, cuánto y a cuánto, en ese orden y sin abreviar el nombre.
// El total de cada línea va alineado a la derecha con la clase `.num` (mono
// tabular): es lo que permite recorrer la columna con la vista y darse cuenta
// de que falta algo.
//
// Y tiene que LEERSE desde donde se lee: a un metro, de costado, en el monitor
// del mostrador. El renglón de «cuánto × a cuánto» estaba en 12,5 px y en el
// gris más tenue de la paleta —el que se usa para lo accesorio— cuando es
// justo el dato que el cliente controla y el cajero canta. Acá ese gris es
// para el código del producto y nada más: lo que se lee de lejos es el nombre,
// la cantidad, el precio unitario y el importe.
export function Ticket({
  lineas,
  seleccionada,
  onSeleccionar,
  onSumar,
  onQuitar,
  onEditarCantidad,
}: Props) {
  const lista = useRef<HTMLUListElement>(null)

  // El ticket SIGUE al renglón que se acaba de cargar.
  //
  // Sin esto, pasados los seis o siete productos la lista no se mueve y el
  // cajero deja de ver lo que agrega: no sabe si entró, ni si entró dos veces,
  // ni con qué cantidad. Y del otro lado del mostrador el cliente está
  // controlando justamente eso, que es para lo que el panel existe.
  //
  // Se mira el LARGO y no el contenido: lo que tiene que mover la lista es que
  // haya un renglón más, no que a uno le cambie la cantidad —ahí el cajero
  // está mirando el renglón que edita y llevarlo al final le sacaría de la
  // vista lo que está corrigiendo—.
  useEffect(() => {
    const caja = lista.current
    if (!caja || lineas.length === 0) return

    // `scrollTop` directo y no `scrollIntoView`: el segundo scrollea a TODOS
    // los ancestros scrolleables, así que en el teléfono —donde la página
    // entera scrollea— se lleva puesta la pantalla además de la lista.
    caja.scrollTop = caja.scrollHeight
  }, [lineas.length])

  if (lineas.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-[15px] font-medium text-tinta-suave">Ticket vacío</p>
        <p className="text-[13px] leading-relaxed text-tinta-tenue">
          Tocá un producto, tipeá su código o su nombre y Enter, o pasá la etiqueta de la balanza
          por el lector. Con varios resultados, las flechas eligen.
        </p>
      </div>
    )
  }

  return (
    <ul ref={lista} className="min-h-0 flex-1 divide-y divide-borde/70 overflow-y-auto">
      {lineas.map((l) => {
        const activa = l.uid === seleccionada
        const conPromo = l.promocionDescuentoCentavos > 0
        const conManual = l.descuentoCentavos > 0

        return (
          <li
            key={l.uid}
            onClick={() => onSeleccionar(l.uid)}
            className={clsx(
              'sin-seleccion cursor-pointer px-3.5 py-3 transition',
              activa ? 'bg-verde-tenue' : 'hover:bg-crema',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              {/* La misma miniatura que la grilla de la izquierda.
                  Sin ella, el renglón del ticket y la tarjeta que se acaba de
                  tocar no se parecen en nada, y el control que el cliente hace
                  —mirar que lo que entró sea lo que pidió— es justamente
                  visual. Con producto sin foto queda la barra de color de su
                  categoría, que es lo que ya hace la tarjeta. */}
              <Miniatura linea={l} />

              <div className="min-w-0 flex-1">
                <p className="truncate text-[15.5px] font-semibold leading-tight text-tinta">
                  {l.producto.nombre}
                </p>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEditarCantidad(l.uid)
                  }}
                  // La cantidad es un botón: tocarla abre el teclado para
                  // corregirla. Es el error más frecuente del mostrador
                  // (pesó de más, cargó dos en lugar de una) y tiene que
                  // arreglarse en un toque, no borrando la línea y
                  // volviéndola a cargar.
                  title="Corregir la cantidad (F4)"
                  className="num mt-1 rounded px-0.5 text-[14.5px] font-medium text-tinta-suave
                             underline decoration-dotted underline-offset-4 hover:text-verde"
                >
                  {cantidadMilesimas(l.cantidadMilesimas, l.producto.decimalesCantidad)}{' '}
                  {l.producto.unidadAbreviatura} × {plataCentavos(l.producto.precioCentavos)}
                </button>

                {conPromo && (
                  <p className="mt-1 flex items-center gap-1 text-[13px] font-medium text-verde-oscuro">
                    <Tag className="h-3.5 w-3.5" />
                    {l.promocionNombre}
                    <span className="num">−{plataCentavos(l.promocionDescuentoCentavos)}</span>
                  </p>
                )}

                {conManual && (
                  <p className="mt-0.5 text-[13px] font-medium text-ambar">
                    Descuento a mano <span className="num">−{plataCentavos(l.descuentoCentavos)}</span>
                  </p>
                )}
              </div>

              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="num text-[17px] font-bold text-tinta">
                  {plataCentavos(totalLinea(l))}
                </span>

                {(conPromo || conManual) && (
                  <span className="num text-[13px] text-tinta-tenue line-through">
                    {plataCentavos(l.brutoCentavos)}
                  </span>
                )}
              </div>
            </div>

            {/* Los controles aparecen solo en la línea activa: con seis líneas
                en pantalla, dieciocho botones son ruido y un toque perdido. */}
            {activa && (
              <div className="mt-2 flex items-center gap-1.5">
                <BotonChico onClick={() => onSumar(l.uid, -1)} etiqueta="Restar (tecla −)">
                  <Minus className="h-4 w-4" />
                </BotonChico>

                <BotonChico onClick={() => onSumar(l.uid, 1)} etiqueta="Sumar (tecla +)">
                  <Plus className="h-4 w-4" />
                </BotonChico>

                <div className="flex-1" />

                <BotonChico onClick={() => onQuitar(l.uid)} etiqueta="Quitar la línea (Supr)" peligro>
                  <Trash2 className="h-4 w-4" />
                </BotonChico>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

// La imagen de un renglón del ticket.
//
// La URL la manda el HOST en el catálogo del POS y acá solo se le pone el
// prefijo (`urlImagen`): la pantalla no arma direcciones de imágenes, porque el
// que sabe cómo se sirve una imagen es el que la sirve. En la nube es
// `/api/uploads/{code}/thumb` —donde el Code aleatorio ES la autorización— y en
// la caja del mostrador es su ruta local, que anda sin internet.
function Miniatura({ linea }: { linea: LineaTicket }) {
  const p = linea.producto

  const foto = p.fotoMiniaturaUrl
    ? api.urlImagen(p.fotoMiniaturaUrl)
    : p.tieneFoto
      ? api.urlImagen(`/api/productos/${p.id}/foto`)
      : null

  if (foto) {
    return (
      <img
        src={foto}
        alt=""
        loading="lazy"
        className="mt-0.5 h-11 w-11 shrink-0 rounded-lg border border-borde/60 bg-crema-alt object-cover"
      />
    )
  }

  return (
    <span
      aria-hidden
      className="mt-1 h-10 w-1.5 shrink-0 rounded-full"
      style={{ background: p.color || p.categoriaColor || '#8B8579' }}
    />
  )
}

function BotonChico({
  onClick,
  etiqueta,
  peligro,
  children,
}: {
  onClick: () => void
  etiqueta: string
  peligro?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={etiqueta}
      title={etiqueta}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={clsx(
        // 40 px de lado: es el mínimo con el que un dedo acierta sin apuntar.
        'grid h-10 w-10 place-items-center rounded-lg border transition active:scale-95',
        peligro
          ? 'border-rojo/25 text-rojo hover:bg-rojo/10'
          : 'border-borde bg-papel text-tinta-suave hover:border-borde-fuerte',
      )}
    >
      {children}
    </button>
  )
}
